import { ClearButton } from "../clear-button/ClearButton.js";
import "../clear-button/index.js";
import { SingleSelectTrigger as SingleSelectTrigger$1 } from "../single-select/SingleSelect.js";
import "../single-select/index.js";
import { twMerge } from "tailwind-merge";
import { jsx, jsxs } from "react/jsx-runtime";
import { createContext, use, useId, useMemo, useRef, useState } from "react";
import { ANY_COUNTRY_PHONE_MASK, DATE_MASK, DECIMAL_MASK, EIN_MASK, NUMERIC_ONLY_MASK, VERIFICATION_CODE_MASK, WHOLE_NUMBER_MASK, maskPhone } from "@givebutter/util";
import { bindForwardedRef } from "@givebutter/react-util";
import { useMaskito } from "@maskito/react";
//#region src/input/Input.tsx
const InputContext = createContext({
	isError: false,
	disabled: false,
	isFocused: false,
	inputRef: null,
	enableDisabledStyle: true,
	/* v8 ignore next 2 */
	setIsFocused: () => {},
	setInputRef: () => {}
});
const InputRoot = ({ ref, className, children, mask, maskOptions, isError = false, disabled = false, enableDisabledStyle = true, size = "md", onMouseDown, onFocus, onClick, ...rest }) => {
	const internalRef = useRef(null);
	const [isFocused, setIsFocused] = useState(false);
	const [inputRef, setInputRef] = useState(null);
	const ctx = useMemo(() => ({
		mask,
		maskOptions,
		disabled,
		enableDisabledStyle,
		isError,
		isFocused,
		inputRef,
		setInputRef,
		setIsFocused
	}), [
		disabled,
		enableDisabledStyle,
		inputRef,
		isError,
		isFocused,
		mask,
		maskOptions
	]);
	return /* @__PURE__ */ jsx(InputContext, {
		value: ctx,
		children: /* @__PURE__ */ jsx("div", {
			ref: (node) => {
				internalRef.current = node;
				bindForwardedRef(ref, node);
			},
			onMouseDown: (e) => {
				if (e.target !== inputRef) e.preventDefault();
				/* v8 ignore next */
				onMouseDown?.(e);
			},
			onClick: (e) => {
				if (e.target !== inputRef) inputRef?.focus();
				/* v8 ignore next */
				onClick?.(e);
			},
			onFocus: (e) => {
				if (e.target === inputRef) inputRef?.focus();
				/* v8 ignore next */
				onFocus?.(e);
			},
			className: twMerge("flex h-fit min-h-6 w-[inherit] cursor-text items-center gap-2 rounded-lg bg-white px-3 text-gray-900 ring-1 ring-gray-300 ring-inset", size === "sm" && "py-1", size === "md" && "py-2", size === "lg" && "py-2.5", enableDisabledStyle && disabled && "cursor-not-allowed bg-gray-50 text-gray-300", (isError || isFocused) && "ring-2", isError && "ring-red-500", isFocused && "ring-blue-500", className),
			...rest,
			children
		})
	});
};
const InputForm = ({ ref, className, disabled, onFocus, onBlur, onPaste, ...rest }) => {
	const { setIsFocused, disabled: disabledCtx, enableDisabledStyle, isError, mask = "none", maskOptions: maskOptionsConsumer, setInputRef } = use(InputContext);
	const _maskOptions = useMemo(() => getMask(mask), [mask]);
	const maskOptions = maskOptionsConsumer ? {
		...maskOptionsConsumer,
		..._maskOptions ?? {}
	} : _maskOptions;
	const inputRef = useMaskito({ options: maskOptions });
	return /* @__PURE__ */ jsx("input", {
		ref: (node) => {
			inputRef(node);
			bindForwardedRef(ref, node);
			setInputRef(node);
		},
		disabled: disabled || disabledCtx,
		className: twMerge("h-full min-h-6 max-w-full grow appearance-none border-none bg-transparent outline-hidden", enableDisabledStyle && "disabled:cursor-not-allowed disabled:placeholder:text-gray-200", "placeholder:text-gray-400", className),
		onFocus: (e) => {
			setIsFocused(true);
			/* v8 ignore next */
			onFocus?.(e);
		},
		onBlur: (e) => {
			setIsFocused(false);
			/* v8 ignore next */
			onBlur?.(e);
		},
		onPaste: (e) => {
			if (mask === "phone") {
				const pastedText = e.clipboardData.getData("text/plain").replace(/\D/g, "");
				if (pastedText.length <= 10) e.target.value = maskPhone(`+1${pastedText}`);
			}
			/* v8 ignore next */
			onPaste?.(e);
		},
		"aria-invalid": isError || void 0,
		...rest
	});
};
const InputInteractable = ({ children, ...rest }) => {
	return /* @__PURE__ */ jsx("div", {
		onMouseDown: (e) => e.stopPropagation(),
		onClick: (e) => e.stopPropagation(),
		...rest,
		children
	});
};
const InputSingleSelectTrigger = ({ className, disabled, ...rest }) => {
	const { disabled: disabledCtx, enableDisabledStyle } = use(InputContext);
	const id = useId();
	return /* @__PURE__ */ jsxs(InputInteractable, { children: [/* @__PURE__ */ jsx("label", {
		htmlFor: id,
		className: "sr-only",
		children: "Select value for input"
	}), /* @__PURE__ */ jsx(SingleSelectTrigger$1, {
		id,
		disabled: disabled || disabledCtx,
		className: twMerge("size-auto bg-transparent p-0 px-2 py-1 ring-0 *:leading-none focus:ring-2", enableDisabledStyle && "disabled:text-gray-300!", className),
		...rest
	})] });
};
const InputClearButton = ({ visible, onClick, "aria-label": ariaLabel = "Clear", ...rest }) => {
	const { inputRef } = use(InputContext);
	return /* @__PURE__ */ jsx(InputInteractable, {
		className: twMerge("flex items-center", !visible && "invisible"),
		children: /* @__PURE__ */ jsx(ClearButton, {
			"aria-label": ariaLabel,
			"aria-hidden": !visible,
			tabIndex: visible ? void 0 : -1,
			onClick: (event) => {
				onClick(event);
				inputRef?.focus();
			},
			...rest
		})
	});
};
const getMask = (type) => {
	switch (type) {
		case "decimal": return DECIMAL_MASK;
		case "whole-number": return WHOLE_NUMBER_MASK;
		case "numeric-only": return NUMERIC_ONLY_MASK;
		case "ein": return EIN_MASK;
		case "verification-code": return VERIFICATION_CODE_MASK;
		case "date": return DATE_MASK;
		case "phone": return ANY_COUNTRY_PHONE_MASK;
		case "none": return;
	}
};
const Root = InputRoot;
const Form = InputForm;
const Interactable = InputInteractable;
const SingleSelectTrigger = InputSingleSelectTrigger;
const ClearButtonAlias = InputClearButton;
//#endregion
export { ClearButtonAlias as ClearButton, Form, InputClearButton, InputContext, InputForm, InputInteractable, InputRoot, InputSingleSelectTrigger, Interactable, Root, SingleSelectTrigger };
